import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@config/firebase';
import { logError, logInfo } from '@utils/logger';

// Collection name for structure data
const COLLECTION_NAME = 'hauteurs_libres';

/**
 * Gets details for a specific structure by reference
 * @param {string} reference - Structure reference ID
 * @returns {Promise<Object>} - Structure details
 */
export const getStructureDetails = async (reference) => {
  if (!reference) {
    throw new Error('Structure reference is required');
  }
    
  try {
    // Get the main structure data
    const structureQuery = query(collection(db, COLLECTION_NAME), where('reference', '==', reference));
    
    const structureSnapshot = await getDocs(structureQuery);
    
    if (structureSnapshot.empty) {
      throw new Error(`Structure with reference ${reference} not found`);
    }
    
    const structureData = structureSnapshot.docs[0].data();
    
    // Return the structure data which already contains details and routes
    return structureData;
  } catch (error) {
    throw new Error(`Failed to get structure details: ${error.message}`);
  }
};

/**
 * Get structures with height restrictions along a route
 * @param {Object} routeLineString - Turf.js linestring representing the route
 * @param {number} bufferDistance - Buffer distance in kilometers
 * @param {Object} filters - Additional filters
 * @returns {Promise<Array>} - Array of structures along the route
 */
export const getStructuresAlongRoute = async (routeLineString, bufferDistance = 0.5, filters = {}) => {
  try {
    // Get all structures
    const structuresRef = collection(db, COLLECTION_NAME);
    let q = structuresRef;
    
    // Apply filters if provided
    if (filters.minHauteur) {
      q = query(q, where('hauteur', '>=', parseFloat(filters.minHauteur)));
    }
    
    if (filters.maxHauteur) {
      q = query(q, where('hauteur', '<=', parseFloat(filters.maxHauteur)));
    }
    
    if (filters.route) {
      q = query(q, where('route', '==', filters.route));
    }
    
    const snapshot = await getDocs(q);
    
    // Filter out metadata document
    const structures = snapshot.docs
      .filter(doc => doc.id !== 'metadata')
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    
    logInfo(`Retrieved ${structures.length} structures from Firestore`);
    
    // If we don't have a route, return all structures
    if (!routeLineString) {
      return structures;
    }
    
    // If we have a route, filter by buffer distance for structures with coordinates
    const structuresWithCoordinates = structures.filter(structure => 
      structure.coordinates?.latitude && structure.coordinates?.longitude
    );
    
    logInfo(`${structuresWithCoordinates.length} structures have coordinates`);
    
    // Return all structures for now - filtering will be done in the route calculator
    return structures;
  } catch (error) {
    logError('Failed to get structures along route:', error);
    throw error;
  }
};

/**
 * Gets the minimum height for a structure
 * @param {Object} structure - Structure data
 * @returns {number} - Minimum height
 */
export const getMinimumHeight = (structure) => {
  // If we have details, find the minimum height across all measurements
  if (structure?.details && Array.isArray(structure.details)) {
    const heights = structure.details
      .flatMap(detail => [
        detail.gauche,
        detail.centre_gauche,
        detail.centre,
        detail.centre_droit,
        detail.droite
      ])
      .filter(height => height !== null && height !== undefined);
    
    if (heights.length > 0) {
      return Math.min(...heights);
    }
  }
  
  // Otherwise, use the main height
  return structure?.hauteur || structure?.properties?.clearance;
};

/**
 * Checks if a vehicle can pass under a structure
 * @param {Object} structure - Structure data
 * @param {number} vehicleHeight - Vehicle height in meters
 * @param {number} safetyMargin - Safety margin in meters
 * @returns {boolean} - True if the vehicle can pass
 */
export const canVehiclePass = (structure, vehicleHeight, safetyMargin = 0.1) => {
  const minHeight = getMinimumHeight(structure);
  return minHeight >= (vehicleHeight + safetyMargin);
};

export default {
  getStructureDetails,
  getStructuresAlongRoute,
  getMinimumHeight,
  canVehiclePass
};